import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MagnifyingGlassIcon, FunnelIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatMoney } from '../utils/currency';
import { exportToCsv } from '../utils/exportCsv';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Pagination from '../components/common/Pagination';

const Rides = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');

  useEffect(() => {
    fetchRides();
  }, [searchParams]);

  const fetchRides = async () => {
    try {
      const page = searchParams.get('page') || 1;
      const status = searchParams.get('status') || statusFilter;
      const params = new URLSearchParams({
        page,
        limit: 10,
        ...(status && { status })
      });

      const response = await api.get(`/admin/rides?${params}`);
      setRides(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to fetch rides');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    params.set('page', '1');
    setSearchParams(params);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Rides</h1>
        <div className="flex items-center gap-3">
          <span className="text-gray-500">{pagination.total} total rides</span>
          <button
            className="btn-secondary flex items-center gap-1"
            onClick={() => exportToCsv(
              '/admin/exports/rides',
              { status: statusFilter || undefined },
              'rides.csv'
            )}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <form onSubmit={handleFilter} className="flex flex-col sm:flex-row gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input sm:w-48"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="arriving">Arriving</option>
            <option value="arrived">Arrived</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button type="submit" className="btn-primary">
            <FunnelIcon className="w-5 h-5 mr-2 inline" />
            Filter
          </button>
        </form>
      </div>

      {/* Rides Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Ride ID</th>
                <th className="table-header">User</th>
                <th className="table-header">Driver</th>
                <th className="table-header">From / To</th>
                <th className="table-header">Fare</th>
                <th className="table-header">Status</th>
                <th className="table-header">Date</th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rides.map((ride) => (
                <tr key={ride._id} className="hover:bg-gray-50">
                  <td className="table-cell">
                    <span className="font-mono text-sm">#{ride._id.slice(-6)}</span>
                  </td>
                  <td className="table-cell">
                    {ride.user ? (
                      <p className="font-medium">{ride.user.firstName} {ride.user.lastName}</p>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="table-cell">
                    {ride.driver ? (
                      <p className="font-medium">{ride.driver.firstName} {ride.driver.lastName}</p>
                    ) : (
                      <span className="text-gray-400">Not assigned</span>
                    )}
                  </td>
                  <td className="table-cell">
                    <div className="max-w-xs">
                      <p className="text-sm truncate">{ride.pickup?.address}</p>
                      <p className="text-xs text-gray-500 truncate">→ {ride.dropoff?.address}</p>
                    </div>
                  </td>
                  <td className="table-cell font-medium">
                    {formatMoney(ride.fare?.totalFare || 0, ride.fare?.currency || 'QAR')}
                  </td>
                  <td className="table-cell"><StatusBadge status={ride.status} /></td>
                  <td className="table-cell text-sm text-gray-500">
                    {new Date(ride.createdAt).toLocaleDateString()}
                  </td>
                  <td className="table-cell">
                    <Link
                      to={`/rides/${ride._id}`}
                      className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                    >
                      View Details
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          page={pagination.page}
          pages={pagination.pages}
          onPageChange={(p) => setSearchParams(prev => { prev.set('page', p); return prev; })}
        />
      </div>
    </div>
  );
};

export default Rides;
